import React, { useEffect, useMemo, useState } from "react";
import { useEditorStore } from "../../../../../state/editorStore";
import { buildDropShadowFilter, scaleShadowForDisplay } from "../../../../../utils/dropShadowFilter";
import { getTransform } from "../../../../../utils/getTransform";
import {
  SHADOW_ENTRY_BACKGROUND,
  TOOL_PREP_PREVIEW_BG,
} from "../../../../../utils/editorEntrySession";
import {
  aspectFromSize,
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useImageNaturalSize,
  useReportSubjectPlacement,
} from "../../../lib/toolPrepImageLayout";
import { useToolPrepSubjectObject } from "../../../hooks/useToolPrepSubjectObject";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepSubjectBoundingBox from "../../../shared/Popup/ToolPrepSubjectBoundingBox";
import {
  ToolPrepSubjectCropGhost,
  subjectMainImageStyleForCrop,
  subjectWrapperStyleForCrop,
} from "../../../shared/Popup/ToolPrepSubjectCropGhost";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "../../../shared/Popup/ToolPrepStage.css";
import "./ShadowPreview.css";

const ShadowPreview = ({
  imageUrl = null,
  uploadedImageUrl = null,
  loading = false,
  applying = false,
  applyingLabel = "Applying shadow…",
  previewBg = TOOL_PREP_PREVIEW_BG.WHITE,
  emptyLabel = "Upload an image to preview",
  showSubjectBox = false,
  onSubjectPlacement = null,
}) => {
  const shadow = useEditorStore((s) => s.object.shadow);
  const naturalSize = useImageNaturalSize(imageUrl || uploadedImageUrl);
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const stageRef = React.useRef(null);
  const isTransparent = previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT;
  const isOriginal = previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL;
  const isEmpty = !imageUrl && !uploadedImageUrl && !loading;
  const useChecker = isTransparent || isEmpty;

  const imageAspect = useMemo(
    () => aspectFromSize(naturalSize.width, naturalSize.height, 1),
    [naturalSize]
  );

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        imageAspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (fitted) setStageSize(fitted);
    };
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [imageAspect, imageUrl]);

  const transform = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height || !stageSize.width) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }
    return getTransform(
      naturalSize.width,
      naturalSize.height,
      stageSize.width,
      stageSize.height,
      "fit"
    );
  }, [naturalSize, stageSize]);

  const fitFrame = useMemo(() => {
    if (!(transform.width > 0)) return null;
    return {
      x: transform.x,
      y: transform.y,
      width: transform.width,
      height: transform.height,
    };
  }, [transform.x, transform.y, transform.width, transform.height]);

  const interactive = showSubjectBox && Boolean(imageUrl) && !loading && !applying;
  const [isCropping, setIsCropping] = useState(false);
  const {
    object,
    setObject,
    wrapperStyle,
    imageStyle,
    contentCrop,
    crop,
    cropExtent,
    originalImageSize,
  } = useToolPrepSubjectObject({
    enabled: interactive,
    imageUrl,
    fitFrame,
    resetKey: imageUrl,
  });

  useReportSubjectPlacement(
    onSubjectPlacement,
    object,
    contentCrop,
    stageSize,
    imageUrl
  );

  // Native px → display px. Prefer crop width when the BB is tightened to opaque bounds.
  const displayScale = useMemo(() => {
    if (!object?.width) return 1;
    if (crop?.width > 0) {
      return object.width / crop.width;
    }
    if (naturalSize.width > 0) {
      return object.width / naturalSize.width;
    }
    return 1;
  }, [crop?.width, naturalSize.width, object?.width]);

  const filter = useMemo(
    () => buildDropShadowFilter(scaleShadowForDisplay(shadow, displayScale)),
    [displayScale, shadow]
  );

  const fitStyle = fitFrame
    ? {
        left: fitFrame.x,
        top: fitFrame.y,
        width: fitFrame.width,
        height: fitFrame.height,
      }
    : undefined;

  return (
    <ToolPrepCanvasStage
      className="shadow-tool-preview"
      ref={stageRef}
      metaImageUrl={imageUrl || uploadedImageUrl}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
    >
      <div
        className={`shadow-preview__stage${
          useChecker ? " shadow-preview__stage--checker" : ""
        }${isOriginal && !isEmpty ? " shadow-preview__stage--original" : ""}`}
        style={{
          width: stageSize.width,
          height: stageSize.height,
          backgroundColor:
            useChecker || isOriginal ? undefined : SHADOW_ENTRY_BACKGROUND,
        }}
      >
        {/*
          --effects: allow drop-shadow past subject/stage edge without growing the frame.
          Filter lives on the wrapper so subject clip-path does not chop the shadow.
        */}
        <div className="tool-prep-stage__clip tool-prep-stage__clip--effects">
          {isOriginal && uploadedImageUrl ? (
            <img
              src={uploadedImageUrl}
              alt=""
              className={
                fitFrame
                  ? "shadow-preview__uploaded-bg"
                  : "shadow-preview__uploaded-bg shadow-preview__uploaded-bg--fallback"
              }
              style={fitStyle}
              draggable={false}
            />
          ) : null}
          {loading ? (
            <div className="shadow-preview__loading">
              <RoundLoader size="md" label="Removing background…" />
            </div>
          ) : applying ? (
            <div className="shadow-preview__loading">
              <RoundLoader size="md" label={applyingLabel} />
            </div>
          ) : imageUrl && interactive && object && wrapperStyle ? (
            <div
              className="shadow-preview__subject"
              style={subjectWrapperStyleForCrop(
                { ...wrapperStyle, overflow: "visible", filter },
                isCropping
              )}
            >
              <ToolPrepSubjectCropGhost
                active={isCropping}
                src={imageUrl}
                style={imageStyle}
              />
              <img
                src={imageUrl}
                alt="Shadow preview"
                className="shadow-preview__image"
                style={subjectMainImageStyleForCrop(imageStyle, {
                  isCropping,
                  crop,
                  originalImageSize,
                  object,
                })}
                draggable={false}
              />
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt="Shadow preview"
              className={
                fitFrame
                  ? "shadow-preview__image"
                  : "shadow-preview__image shadow-preview__image--fallback"
              }
              style={fitStyle ? { ...fitStyle, filter } : { filter }}
              draggable={false}
            />
          ) : (
            <div className="shadow-preview__empty">{emptyLabel}</div>
          )}
        </div>
        {imageUrl && interactive && object && wrapperStyle ? (
          <div className="tool-prep-stage__chrome">
            <ToolPrepSubjectBoundingBox
              enabled
              interactive
              frame={object}
              crop={crop}
              cropExtent={cropExtent}
              originalImageSize={originalImageSize}
              onChange={setObject}
              onCroppingChange={setIsCropping}
              stageSize={stageSize}
            />
          </div>
        ) : null}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default ShadowPreview;
